import { Button, IntegerField } from "@dash/form";
import { Card, Page, PageHeader, SectionHeader } from "@dash/page";
import { SelectDialog } from "@dash/select";
import { QuerySkeleton } from "@dash/skeleton";
import { RowCellContent, Table, TableBody, TableHeadColumn } from "@dash/table";
import { useTheme } from "@emotion/react";
import { useMemo, useState } from "react";
import { MdDeleteOutline } from "react-icons/md";

import { UnitTemplate, unitTemplateComponentsSerializer } from "@megaron/shipping-contracts";
import { useClientManager } from "@megaron/react-clients";

type Counts = Record<string, number>;

const selectionColumns: TableHeadColumn<string>[] = [
  { label: "Jednostka", isSortable: false },
  { label: "Komponenty", isSortable: false },
  { label: "Ilość", isSortable: false },
  { label: "", isSortable: false },
];

const inputColumns: TableHeadColumn<string>[] = [
  { label: "Jednostka", isSortable: false },
  { label: "Ilość", isSortable: false },
  { label: "", isSortable: false },
];

const balanceColumns: TableHeadColumn<string>[] = [
  { label: "Jednostka", isSortable: false },
  { label: "Bilans", isSortable: false, cellCss: { textAlign: "right" } },
  { label: "Status", isSortable: false },
];

export const CalculatorDebugPage: React.FC = () => {
  const theme = useTheme();
  const templatesQuery = useClientManager("shipping").unitTemplateQuery().useQuery({});
  const [templateCounts, setTemplateCounts] = useState<Counts>({});
  const [inputCounts, setInputCounts] = useState<Counts>({});

  const templates = templatesQuery.data?.units ?? [];
  const templateOptions = useMemo(
    () => templates.map((template) => ({ value: template.id, label: `${template.label} (${template.unit})` })),
    [templates],
  );
  const unitOptions = useMemo(
    () =>
      [...new Set(templates.flatMap((template) => [template.unit, ...Object.keys(template.components)]))]
        .sort((left, right) => left.localeCompare(right, "pl"))
        .map((unit) => ({ value: unit, label: unit })),
    [templates],
  );
  const selectedTemplates = templates.filter((template) => (templateCounts[template.id] ?? 0) > 0);
  const selectedInputUnits = Object.entries(inputCounts).filter(([, quantity]) => quantity > 0);
  const balance = useMemo(
    () => calculateBalance(selectedTemplates, templateCounts, inputCounts),
    [selectedTemplates, templateCounts, inputCounts],
  );

  const updateTemplateCount = (templateId: string, quantity: number) =>
    setTemplateCounts((counts) => updateCount(counts, templateId, quantity));
  const updateInputCount = (unit: string, quantity: number) =>
    setInputCounts((counts) => updateCount(counts, unit, quantity));
  const addTemplate = (templateId: string) =>
    setTemplateCounts((counts) => updateCount(counts, templateId, (counts[templateId] ?? 0) + 1));
  const addInputUnit = (unit: string) => setInputCounts((counts) => updateCount(counts, unit, (counts[unit] ?? 0) + 1));

  const templateRows = selectedTemplates.map((template) => ({
    id: template.id,
    cellsContent: templateSelectionCells(template, templateCounts[template.id] ?? 0, updateTemplateCount),
  }));
  const inputRows = selectedInputUnits.map(([unit, quantity]) => ({
    id: unit,
    cellsContent: inputSelectionCells(unit, quantity, updateInputCount),
  }));
  const balanceRows = Object.entries(balance).map(([unit, quantity]) => {
    const isLeftover = quantity > 0;
    const cellsContent: RowCellContent[] = [
      { element: unit },
      {
        element: <strong css={{ color: isLeftover ? theme.colors.success : theme.colors.danger }}>{quantity}</strong>,
        cellCss: { textAlign: "right" },
      },
      { element: isLeftover ? "pozostało" : "brakuje" },
    ];

    return { id: unit, cellsContent };
  });

  return (
    <Page css={{ maxWidth: "none", boxSizing: "border-box", display: "flex", flexDirection: "column", gap: "1rem" }}>
      <PageHeader
        actions={
          <Button
            variant="outline"
            onClick={() => {
              setTemplateCounts({});
              setInputCounts({});
            }}
          >
            Wyczyść
          </Button>
        }
      >
        Debug kalkulatora wysyłki
      </PageHeader>

      <p css={{ margin: 0, color: theme.colors.secondaryText }}>
        Bilans jest wyliczany lokalnie: szablon dodaje swoją jednostkę i odejmuje komponenty, a jednostki wejściowe są
        dodawane do bilansu.
      </p>

      <QuerySkeleton query={templatesQuery}>
        {() => (
          <>
            <DebugSection title="Szablony">
              <AddUnitForm label="Dodaj szablon" options={templateOptions} onSelect={addTemplate} />
              {templateRows.length > 0 ? (
                <Table columns={selectionColumns}>
                  <TableBody rows={templateRows} />
                </Table>
              ) : (
                <EmptyState>Nie dodano szablonów.</EmptyState>
              )}
            </DebugSection>

            <DebugSection title="Jednostki wejściowe">
              <AddUnitForm label="Dodaj jednostkę" options={unitOptions} onSelect={addInputUnit} />
              {inputRows.length > 0 ? (
                <Table columns={inputColumns}>
                  <TableBody rows={inputRows} />
                </Table>
              ) : (
                <EmptyState>Nie dodano jednostek wejściowych.</EmptyState>
              )}
            </DebugSection>

            <DebugSection title="Bilans jednostek">
              {balanceRows.length > 0 ? (
                <Table columns={balanceColumns}>
                  <TableBody rows={balanceRows} />
                </Table>
              ) : (
                <EmptyState>Dodaj szablon lub jednostkę wejściową, aby zobaczyć bilans.</EmptyState>
              )}
            </DebugSection>
          </>
        )}
      </QuerySkeleton>
    </Page>
  );
};

const DebugSection = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div css={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
    <SectionHeader isHr>{title}</SectionHeader>
    <Card variant="secondary" css={{ gap: "1rem", overflow: "auto" }}>
      {children}
    </Card>
  </div>
);

const EmptyState = ({ children }: { children: React.ReactNode }) => <span css={{ opacity: 0.6 }}>{children}</span>;

const AddUnitForm = ({
  label,
  options,
  onSelect,
}: {
  label: string;
  options: { label: string; value: string }[];
  onSelect: (value: string) => void;
}) => {
  const [selectionKey, setSelectionKey] = useState(0);

  return (
    <div css={{ maxWidth: "42rem" }}>
      <SelectDialog
        key={selectionKey}
        label={label}
        options={options}
        variant="single-select"
        onSelect={(option) => {
          if (!option) return;

          onSelect(option.value);
          setSelectionKey((key) => key + 1);
        }}
      />
    </div>
  );
};

const templateSelectionCells = (
  template: UnitTemplate,
  quantity: number,
  onChange: (templateId: string, quantity: number) => void,
): RowCellContent[] => [
  { element: `${template.label} (${template.unit})` },
  { element: unitTemplateComponentsSerializer.serialize(template.components) },
  {
    element: <IntegerField value={quantity} onChange={(value) => onChange(template.id, Math.max(0, value))} />,
    cellCss: { minWidth: "7rem" },
  },
  {
    element: (
      <Button
        size="small"
        variant="text"
        color="danger"
        icon={<MdDeleteOutline />}
        onClick={() => onChange(template.id, 0)}
      />
    ),
    cellCss: { width: "1%" },
  },
];

const inputSelectionCells = (
  unit: string,
  quantity: number,
  onChange: (unit: string, quantity: number) => void,
): RowCellContent[] => [
  { element: unit },
  {
    element: <IntegerField value={quantity} onChange={(value) => onChange(unit, Math.max(0, value))} />,
    cellCss: { minWidth: "7rem" },
  },
  {
    element: (
      <Button size="small" variant="text" color="danger" icon={<MdDeleteOutline />} onClick={() => onChange(unit, 0)} />
    ),
    cellCss: { width: "1%" },
  },
];

const updateCount = (counts: Counts, key: string, quantity: number): Counts => {
  if (quantity === 0) {
    const { [key]: _, ...remainingCounts } = counts;
    return remainingCounts;
  }

  return { ...counts, [key]: quantity };
};

const calculateBalance = (templates: UnitTemplate[], templateCounts: Counts, inputCounts: Counts): Counts => {
  const balance = { ...inputCounts };

  for (const template of templates) {
    const quantity = templateCounts[template.id] ?? 0;
    balance[template.unit] = (balance[template.unit] ?? 0) + quantity;

    for (const [unit, componentQuantity] of Object.entries(template.components)) {
      balance[unit] = (balance[unit] ?? 0) - componentQuantity * quantity;
    }
  }

  return Object.fromEntries(
    Object.entries(balance)
      .filter(([, quantity]) => quantity !== 0)
      .sort(([left], [right]) => left.localeCompare(right, "pl")),
  );
};
